import { keepPreviousData } from '@tanstack/react-query'
import type { components } from '@joyful/openapi/types.gen'
import { formatTimestamp } from '@joyful/utils'
import { ChartLineIcon } from 'lucide-react'

import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandLoading,
} from '../../../core'
import { getCurrentNavbarApp, getNavbarAppHref } from '../navbar/Navbar'
import { useApiOrgSlug } from '../../utils/orgSlug'
import { useApiQuery } from '../../utils/reactQuery'

export interface AnalysisPageEntitySearchResultsProps {
  onSelect: () => void
  query: string
}

export function AnalysisPageEntitySearchResults({
  onSelect,
}: AnalysisPageEntitySearchResultsProps) {
  const orgSlug = useApiOrgSlug()
  const libraryQuery = useApiQuery(
    'get',
    '/internal/analysis/{account_slug}',
    {
      params: {
        path: {
          account_slug: orgSlug,
        },
      },
    },
    {
      placeholderData: keepPreviousData,
    },
  )

  if (libraryQuery.isLoading) {
    return <CommandLoading>{`Loading…`}</CommandLoading>
  }

  if (libraryQuery.isError) {
    return (
      <CommandEmpty padding="compact" size="xs">
        {`Unable to load artifacts.`}
      </CommandEmpty>
    )
  }

  if (!libraryQuery.isSuccess) {
    return null
  }

  const created = libraryQuery.data.created
  const sharedWithMe = libraryQuery.data.shared_with_me

  if (created.length === 0 && sharedWithMe.length === 0) {
    return (
      <CommandEmpty padding="compact" size="xs">
        {`No artifacts found.`}
      </CommandEmpty>
    )
  }

  return (
    <>
      <CommandEmpty padding="compact" size="xs">
        {`No artifacts found.`}
      </CommandEmpty>
      {created.length > 0 ? (
        <CommandGroup heading={`Created`}>
          {created.map((analysisPage) => {
            return (
              <AnalysisPageCommandItem
                key={analysisPage.id}
                analysisPage={analysisPage}
                origin="created"
                onSelect={onSelect}
              />
            )
          })}
        </CommandGroup>
      ) : null}
      {sharedWithMe.length > 0 ? (
        <CommandGroup heading={`Shared with me`}>
          {sharedWithMe.map((analysisPage) => {
            return (
              <AnalysisPageCommandItem
                key={analysisPage.id}
                analysisPage={analysisPage}
                origin="shared_with_me"
                onSelect={onSelect}
              />
            )
          })}
        </CommandGroup>
      ) : null}
    </>
  )
}

function AnalysisPageCommandItem({
  analysisPage,
  onSelect,
  origin,
}: {
  analysisPage: components['schemas']['AnalysisLibraryItem']
  onSelect: () => void
  origin: 'created' | 'shared_with_me'
}) {
  const title = analysisPage.title.trim() || `Untitled analysis`
  const navbarApp = getCurrentNavbarApp()
  const analysisPath =
    navbarApp === 'internal'
      ? `/${analysisPage.account_slug}/internal/analysis/${analysisPage.id}`
      : `/${analysisPage.account_slug}/analysis/${analysisPage.id}`

  return (
    <CommandItem
      size="xl"
      animation="none"
      value={analysisPage.id}
      keywords={[
        title,
        analysisPage.account_name,
        `analysis`,
        origin === 'shared_with_me' ? `shared` : `created`,
      ]}
      startAdornment={<ChartLineIcon className="size-4" />}
      subtitle={formatTimestamp(analysisPage.modified_at, {
        format: 'adaptive',
      })}
      onSelect={() => {
        onSelect()
        globalThis.location.assign(
          getNavbarAppHref({
            app: navbarApp === 'internal' ? 'internal' : 'app',
            path: analysisPath,
          }),
        )
      }}
    >
      {title}
    </CommandItem>
  )
}
